import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Col, ControlLabel, Glyphicon } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorMessage from 'components/forms/_errorMessage/ErrorMessage';
import { FormGroupContainer } from 'components/forms/genericFormField/GenericFormField.styles';
import TextField from 'components/forms/textField/TextField';
import Tooltip from 'components/tooltip/Tooltip';
import { SocialPlatform } from 'models/DbsSettings';
import {
  SUPPORTED_SOCIAL_PLATFORMS,
  SupportedSocialPlatform,
} from '../WebsiteSettingsTab.schema';
import {
  SOCIAL_LABEL_ID,
  SOCIAL_PLATFORM_PROPS,
  SOCIAL_TABPANEL_ID,
  getSocialTabId,
} from './SocialIcons.constants';
import {
  ConfiguredDotStyled,
  SocialIconToggleStyled,
  SocialIconsGridStyled,
  SocialIconsRowStyled,
  SocialIconsUrlStyled,
} from './SocialIcons.styles';

const ARROW_OFFSETS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };

const SocialIconsField = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [selectedPlatform, setSelectedPlatform] =
    useState<SupportedSocialPlatform>(SocialPlatform.FACEBOOK);
  const socialLinks: Record<string, string> =
    useWatch({ control, name: 'socialLinks' }) ?? {};
  const panelRef = useRef<HTMLDivElement>(null);
  const shouldFocusUrl = useRef(false);

  useEffect(() => {
    if (!shouldFocusUrl.current) {
      return;
    }

    shouldFocusUrl.current = false;
    panelRef.current?.querySelector('input')?.focus();
  }, [selectedPlatform]);

  const selectPlatform = (
    platform: SupportedSocialPlatform,
    focusUrl: boolean,
  ) => {
    shouldFocusUrl.current = focusUrl;
    setSelectedPlatform(platform);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const offset = ARROW_OFFSETS[event.key];
    if (!offset) {
      return;
    }

    event.preventDefault();
    const { length } = SUPPORTED_SOCIAL_PLATFORMS;
    const index = SUPPORTED_SOCIAL_PLATFORMS.indexOf(selectedPlatform);
    const next = SUPPORTED_SOCIAL_PLATFORMS[(index + offset + length) % length];

    selectPlatform(next, false);
    document
      .getElementById(getSocialTabId(SOCIAL_PLATFORM_PROPS[next].key))
      ?.focus();
  };

  const selectedKey = SOCIAL_PLATFORM_PROPS[selectedPlatform].key;
  const selectedName = t(
    `pageDirectBookingEngine.websiteSettings.social.${selectedKey}`,
  );

  return (
    <FormGroupContainer $name="socialIcons">
      <Col
        componentClass={ControlLabel}
        data-testid="socialIcons-field-label"
        htmlFor={`socialLinks.${selectedPlatform}`}
        id={SOCIAL_LABEL_ID}
        sm={5}
        xs={4}
      >
        {t('pageDirectBookingEngine.websiteSettings.social.label')}
      </Col>
      <Col sm={7} xs={8}>
        <SocialIconsRowStyled>
          <SocialIconsGridStyled
            aria-labelledby={SOCIAL_LABEL_ID}
            role="tablist"
          >
            {SUPPORTED_SOCIAL_PLATFORMS.map((platform) => {
              const { key, Icon } = SOCIAL_PLATFORM_PROPS[platform];
              const isSelected = platform === selectedPlatform;
              const isConfigured = !!socialLinks[platform]?.trim();
              const name = t(
                `pageDirectBookingEngine.websiteSettings.social.${key}`,
              );

              return (
                <SocialIconToggleStyled
                  $isConfigured={isConfigured}
                  $isSelected={isSelected}
                  aria-controls={SOCIAL_TABPANEL_ID}
                  aria-label={
                    isConfigured
                      ? t(
                          'pageDirectBookingEngine.websiteSettings.social.tabConfigured',
                          { platform: name },
                        )
                      : name
                  }
                  aria-selected={isSelected}
                  data-testid={`social-icon-${key}`}
                  id={getSocialTabId(key)}
                  key={platform}
                  onClick={() => selectPlatform(platform, true)}
                  onKeyDown={handleKeyDown}
                  role="tab"
                  tabIndex={isSelected ? 0 : -1}
                  title={t(
                    'pageDirectBookingEngine.websiteSettings.social.editUrl',
                    { platform: name },
                  )}
                  type="button"
                >
                  <Icon />
                  {isConfigured && !isSelected && (
                    <ConfiguredDotStyled
                      aria-hidden="true"
                      data-testid={`social-icon-${key}-configured`}
                    />
                  )}
                </SocialIconToggleStyled>
              );
            })}
          </SocialIconsGridStyled>
          <Tooltip
            id="socialIcons-tooltip"
            text={t('pageDirectBookingEngine.websiteSettings.social.tooltip')}
          >
            <Glyphicon
              data-testid="socialIcons-tooltip-icon"
              glyph="info-sign"
            />
          </Tooltip>
        </SocialIconsRowStyled>
        <SocialIconsUrlStyled
          aria-labelledby={getSocialTabId(selectedKey)}
          id={SOCIAL_TABPANEL_ID}
          ref={panelRef}
          role="tabpanel"
        >
          <TextField
            aria-label={t(
              'pageDirectBookingEngine.websiteSettings.social.urlLabel',
              { platform: selectedName },
            )}
            key={selectedPlatform}
            name={`socialLinks.${selectedPlatform}`}
            placeholder={t(
              'pageDirectBookingEngine.websiteSettings.social.placeholder',
            )}
          />
          <ErrorMessage error={errors.socialLinks?.message as string} />
        </SocialIconsUrlStyled>
      </Col>
    </FormGroupContainer>
  );
};

export default SocialIconsField;
